import { http, fallback, type Transport } from 'viem';
import { base } from 'viem/chains';

/** Public fallbacks when `VITE_BASE_RPC_URL` is unset or rate-limited. */
const PUBLIC_FALLBACK_RPC_URLS = [
  'https://base-rpc.publicnode.com',
  'https://base.drpc.org',
  'https://mainnet.base.org',
] as const;

/**
 * Prefer `VITE_BASE_RPC_URL` (Alchemy with domain allowlist in prod), then public fallbacks.
 * Avoid leading with `mainnet.base.org` — it 429s quickly under heavy `eth_call` load.
 */
function buildBaseRpcUrls(): readonly string[] {
  const keyed = import.meta.env.VITE_BASE_RPC_URL?.trim();
  if (keyed) {
    return [keyed, ...PUBLIC_FALLBACK_RPC_URLS];
  }
  return PUBLIC_FALLBACK_RPC_URLS;
}

export const BASE_RPC_URLS = buildBaseRpcUrls();

/** True when the primary browser RPC is not one of the built-in public fallbacks. */
export function usesPaidBaseRpc(): boolean {
  const first = BASE_RPC_URLS[0];
  return Boolean(first && !(PUBLIC_FALLBACK_RPC_URLS as readonly string[]).includes(first));
}

export function createBaseFallbackTransport(): Transport {
  return fallback(
    BASE_RPC_URLS.map((url) =>
      http(url, {
        timeout: 12_000,
        retryCount: 1,
        retryDelay: 400,
      }),
    ),
    { rank: true },
  );
}

export const baseChainWithFallbackTransport = {
  ...base,
  rpcUrls: {
    default: { http: [...BASE_RPC_URLS] },
    public: { http: [...BASE_RPC_URLS] },
  },
} as const;
